import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

export interface Option {
  id: string;
  name?: string;
  title?: string;
  description?: string | null;
  image_url?: string | null;
}

interface Props {
  items: Option[];
  selectedId: string | null;
  onSelect: (item: Option) => void;
  testidPrefix: string;
  emptyLabel?: string;
}

export const OptionGrid: React.FC<Props> = ({ items, selectedId, onSelect, testidPrefix, emptyLabel }) => {
  if (items.length === 0)
    return (
      <div className="border border-dashed border-white/15 py-16 text-center text-muted-foreground" data-testid={`${testidPrefix}-empty`}>
        {emptyLabel ?? "Nessuna opzione disponibile al momento."}
      </div>
    );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testidPrefix}-grid`}>
      {items.map((item, i) => {
        const label = item.name ?? item.title ?? "";
        const selected = selectedId === item.id;
        return (
          <motion.button
            key={item.id}
            data-testid={`${testidPrefix}-option-${i}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            onClick={() => onSelect(item)}
            className={`group relative flex flex-col justify-between text-left p-6 min-h-[160px] border transition-all duration-200 ${
              selected
                ? "border-primary bg-primary/[0.06]"
                : "border-white/10 bg-card hover:border-secondary hover:-translate-y-[3px]"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className={`text-[11px] font-mono tracking-[0.2em] transition-colors ${
                selected ? "text-primary" : "text-muted-foreground/40 group-hover:text-secondary"
              }`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className={`h-2 w-2 rounded-full transition-colors ${
                selected ? "bg-primary" : "bg-white/10 group-hover:bg-secondary"
              }`} />
            </div>

            <div className="mt-6">
              <p className={`font-display font-black leading-[0.95] tracking-tight text-xl md:text-2xl break-words transition-colors ${
                selected ? "text-primary" : "text-foreground group-hover:text-secondary"
              }`}>
                {label}
              </p>
              {item.description && (
                <p className="text-xs md:text-sm text-muted-foreground mt-3 leading-relaxed">{item.description}</p>
              )}
            </div>

            {selected && (
              <span className="absolute top-4 right-4 bg-primary text-primary-foreground p-1.5">
                <Check className="h-3.5 w-3.5" />
              </span>
            )}
          </motion.button>
        );
      })}
    </div>
  );
};
